import { CalendarBM, CalendarData, ContentType } from '@bm-js/atlascloud-shared'
import FullCalendar from '@fullcalendar/react'
import dayGridPlugin from '@fullcalendar/daygrid'
import timeGridPlugin from '@fullcalendar/timegrid'
import { useMemo } from 'react'
import {
  EventInput,
  EventSourceInput,
  WeekNumberContentArg,
} from '@fullcalendar/core/index.js'
import dayjs from 'dayjs'
import { useWebsite } from '../utils/hooks'

type Props = {
  content: ContentType<CalendarData>
}

const CalendarContent = ({ content }: Props) => {
  const { atlasWebsite } = useWebsite()
  const calendars = atlasWebsite?.calendars
  const calendarEvents = useMemo(() => {
    if (!calendars?.[0]?.items?.length) {
      return []
    }
    const items = calendars[0].items
    const nonRepeatingItems = items.filter((item) => !item.repeatOptions?.unit)
    const repeatingItems = items.filter((item) => item.repeatOptions?.unit)
    const nonDuplicatedRepeatingItems = repeatingItems
      .sort((a, b) => (dayjs(a.start).isBefore(dayjs(b.start)) ? -1 : 1))
      .filter((item, index) => {
        const firstIndex = repeatingItems.findIndex(
          (i) => i.repeatKey === item.repeatKey
        )
        return firstIndex === index
      })

    const reRepeatedRepeatingItems = nonDuplicatedRepeatingItems
      .map((item) => {
        const { start, end, repeatOptions } = item
        const { unit: _unit, repeatUntil } = repeatOptions
        const unit = _unit as 'day' | 'week'

        const events: CalendarBM['items'] = []

        let currentStart = dayjs(start)
        let currentEnd = dayjs(end)
        const repeatUntilDate = dayjs(repeatUntil)

        while (
          currentStart.isBefore(repeatUntilDate) ||
          currentStart.isSame(repeatUntilDate, 'day')
        ) {
          events.push({
            ...item,
            start: currentStart.toISOString(),
            end: currentEnd.toISOString(),
          })

          currentStart = currentStart.add(1, unit)
          currentEnd = currentEnd.add(1, unit)
        }
        return events
      })
      .flat()

    const finalItems = [...nonRepeatingItems, ...reRepeatedRepeatingItems]
    return finalItems
      .map((item) => {
        return {
          ...item,
          color: atlasWebsite?.websiteConfig.colors.base,
          id: `${item._id}-${item.summary}-${item.start}-${item.end}`,
        }
      })
      .filter((item) => {
        const dateStr = dayjs(item.start).format('YYYY-MM-DD')
        if (!item.repeatOptions?.cancelledDates) return true
        return !item.repeatOptions.cancelledDates.includes(dateStr)
      })
  }, [calendars, atlasWebsite])

  const formattedEvents = useMemo<EventSourceInput>(() => {
    return calendarEvents.map((e) => {
      const event: EventInput = {
        title: e.summary,
        start: e.start,
        end: e.end,
      }
      return event
    })
  }, [calendarEvents])

  return (
    <div
      style={{
        marginBottom: content.data.marginBottom,
      }}
    >
      <FullCalendar
        allDaySlot={false}
        plugins={[dayGridPlugin, timeGridPlugin]}
        initialView='dayGridMonth'
        firstDay={1}
        weekNumbers={true}
        weekNumberContent={(arg: WeekNumberContentArg) => {
          return `V${arg.num}`
        }}
        eventClassNames={['text-xs']}
        headerToolbar={{
          left: 'title',
          center: '',
          right: 'today prev,next dayGridMonth,timeGridWeek,timeGridDay',
        }}
        locale={'sv'}
        buttonText={{
          today: 'Idag',
          month: 'Månad',
          week: 'Vecka',
          day: 'Dag',
        }}
        events={formattedEvents}
        scrollTime={'10:00:00'}
      />
    </div>
  )
}

export default CalendarContent
